/**
 * CandidateQuickCreateDialog - AI-first candidate creation dialog
 *
 * Primary path: Upload resume or paste LinkedIn URL → AI extracts info → review → create
 * Fallback path: Fill name/email manually → create
 * Follows Quick Create Dialog pattern: redirects to detail page for full editing
 */
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Sparkles, Linkedin, Loader2, FileUp } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Separator } from '../../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DialogForm, ValidatedEmailInput } from '../../../shared/components/common';
import { FileUpload } from '@/shared/files/components/FileUpload';
import { useNotification } from '../../../shared/context';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useCreateCandidate, CandidateCreate } from '../hooks/useCandidates';
import { EmployeePicker } from './EmployeePicker';
import { JobPicker } from './JobPicker';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { useCreateApplication } from '../hooks/useApplications';
import { candidateAiApi, CANDIDATE_SOURCE_VALUES as CANDIDATE_SOURCES, type CandidateExtractedData, type Candidate, type CandidateSourceType } from '../services/recruitingApi';

interface CandidateQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill job from context */
  defaultJobId?: string;
  /** Callback when candidate is created (before navigation) */
  onCreated?: (candidate: Candidate) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface QuickCreateFormData {
  first_name: string;
  last_name: string;
  email: string;
  job_id?: string;
  candidate_source?: string;
  agency_organization_id?: string;
  referred_by_employee_id?: string;
}

/** Fields extractable by AI — only name/email shown in dialog, rest passed in create payload */
const EXTRACTABLE_FIELDS: Array<[keyof CandidateExtractedData, keyof CandidateExtractedData]> = [
  ['first_name', 'first_name'],
  ['last_name', 'last_name'],
  ['email', 'email'],
  ['phone', 'phone'],
  ['current_title', 'current_title'],
  ['current_company', 'current_company'],
  ['years_experience', 'years_experience'],
  ['city', 'city'],
  ['state', 'state'],
  ['country', 'country'],
  ['linkedin_url', 'linkedin_url'],
  ['github_url', 'github_url'],
  ['portfolio_url', 'portfolio_url'],
];

export function CandidateQuickCreateDialog({
  open,
  onClose,
  defaultJobId,
  onCreated,
  skipNavigation = false,
}: CandidateQuickCreateDialogProps) {
  const { t } = useTranslation(['recruiting', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotification();
  const { handleMutationError } = useApiError();
  const createMutation = useCreateCandidate();
  const createApplicationMutation = useCreateApplication();

  // AI extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionSource, setExtractionSource] = useState<'resume' | 'linkedin' | null>(null);
  const [linkedinInput, setLinkedinInput] = useState('');
  const [resumeFileId, setResumeFileId] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<CandidateExtractedData | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      job_id: defaultJobId || undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        job_id: defaultJobId || undefined,
      });
      setLinkedinInput('');
      setResumeFileId(null);
      setExtractedData(null);
      setIsExtracting(false);
      setExtractionSource(null);
    }
  }, [open, defaultJobId, reset]);

  /** Apply extracted data to form fields (only fills empty ones) and store full extracted data */
  const applyExtractedData = useCallback((data: CandidateExtractedData) => {
    setExtractedData((prev) => ({ ...prev, ...data }));

    // Fill visible form fields if empty
    const formFieldMap: Array<[keyof CandidateExtractedData, keyof QuickCreateFormData]> = [
      ['first_name', 'first_name'],
      ['last_name', 'last_name'],
      ['email', 'email'],
    ];

    let fieldsSet = 0;
    for (const [extractedKey, formKey] of formFieldMap) {
      const extracted = data[extractedKey];
      if (extracted == null) continue;
      const currentValue = getValues(formKey);
      if (!currentValue) {
        setValue(formKey, String(extracted), { shouldDirty: true, shouldValidate: true });
        fieldsSet++;
      }
    }

    // Count meaningfully extracted fields (non-visible ones included). Exclude
    // linkedin_url: it is the URL the user typed, echoed back, so it is always
    // present and would falsely report "extracted" when the profile was private
    // or unindexed and nothing real came back.
    let totalExtracted = 0;
    for (const [key] of EXTRACTABLE_FIELDS) {
      if (key === 'linkedin_url') continue;
      if (data[key] != null) totalExtracted++;
    }

    return { fieldsSet, totalExtracted };
  }, [setValue, getValues]);

  const handleExtractFromResume = useCallback(async (fileId: string) => {
    setIsExtracting(true);
    setExtractionSource('resume');
    try {
      const data = await candidateAiApi.extractFromResume(fileId);
      const { totalExtracted } = applyExtractedData(data);
      if (totalExtracted > 0) {
        showSuccess(t('candidates.ai.extractSuccess'));
      } else {
        showInfo(t('candidates.ai.extractNone'));
      }
    } catch (error) {
      handleMutationError(error, setError);
    } finally {
      setIsExtracting(false);
      setExtractionSource(null);
    }
  }, [applyExtractedData, showSuccess, showInfo, handleMutationError, setError, t]);

  const handleExtractFromLinkedin = useCallback(async () => {
    const url = linkedinInput.trim();
    if (!url || !url.includes('linkedin.com/in/')) {
      showError(t('candidates.ai.linkedinUrlInvalid'));
      return;
    }

    setIsExtracting(true);
    setExtractionSource('linkedin');
    try {
      const data = await candidateAiApi.extractFromLinkedin(url);
      // Always store the LinkedIn URL
      const dataWithUrl = { ...data, linkedin_url: data.linkedin_url || url };
      const { totalExtracted } = applyExtractedData(dataWithUrl);
      if (totalExtracted > 0) {
        showSuccess(t('candidates.ai.extractSuccess'));
      } else {
        showInfo(t('candidates.ai.extractNone'));
      }
    } catch (error) {
      handleMutationError(error, setError);
    } finally {
      setIsExtracting(false);
      setExtractionSource(null);
    }
  }, [linkedinInput, applyExtractedData, showSuccess, handleMutationError, setError, showInfo, t]);

  const onSubmit = async (data: QuickCreateFormData) => {
    // Build payload with visible fields + all extracted background data
    const createData: CandidateCreate = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email || undefined,
      candidate_source: (data.candidate_source as CandidateSourceType) || undefined,
      agency_organization_id: (data.candidate_source === 'agency' || data.candidate_source === 'outsourcing') ? (data.agency_organization_id || undefined) : undefined,
      referred_by_employee_id: (data.candidate_source === 'employee_referral' || data.candidate_source === 'employee_outbound')
        ? (data.referred_by_employee_id || undefined) : undefined,
      resume_file_id: resumeFileId || undefined,
      // Include all extracted fields that aren't already in the form
      phone: extractedData?.phone || undefined,
      current_title: extractedData?.current_title || undefined,
      current_company: extractedData?.current_company || undefined,
      years_experience: extractedData?.years_experience ?? undefined,
      city: extractedData?.city || undefined,
      state: extractedData?.state || undefined,
      country: extractedData?.country || undefined,
      linkedin_url: extractedData?.linkedin_url || undefined,
      github_url: extractedData?.github_url || undefined,
      portfolio_url: extractedData?.portfolio_url || undefined,
    };

    const newCandidate = await createMutation.mutateAsync(createData);

    // If a job was selected, create an application
    if (data.job_id) {
      await createApplicationMutation.mutateAsync({
        candidate_id: newCandidate.id,
        job_posting_id: data.job_id,
      });
    }

    onCreated?.(newCandidate);
    onClose();

    if (!skipNavigation) {
      navigate(`/recruiting/candidates/${newCandidate.id}`);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('candidates.addCandidate')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="md"
    >
      <div className="space-y-5">
        {/* AI Import Section */}
        <div className="rounded-lg border border-primary/20 bg-primary/[0.02] p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{t('candidates.ai.importTitle')}</span>
          </div>

          {/* Resume Upload */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileUp className="h-3.5 w-3.5 text-muted-foreground" />
              <Label className="text-sm">{t('candidates.fields.resume')}</Label>
            </div>
            <FileUpload
              isUserFile={false}
              accept=".pdf,.doc,.docx"
              maxSizeMB={10}
              onUploadComplete={(fileId) => {
                setResumeFileId(fileId);
                handleExtractFromResume(fileId);
              }}
            />
            {isExtracting && extractionSource === 'resume' && (
              <div className="flex items-center gap-2 text-sm text-primary">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('candidates.ai.extracting')}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">{t('candidates.ai.orSeparator')}</span>
            <Separator className="flex-1" />
          </div>

          {/* LinkedIn URL */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Linkedin className="h-3.5 w-3.5 text-muted-foreground" />
              <Label className="text-sm">{t('candidates.fields.linkedin')}</Label>
            </div>
            <div className="flex gap-2">
              <Input
                value={linkedinInput}
                onChange={(e) => setLinkedinInput(e.target.value)}
                placeholder={t('candidates.placeholders.linkedinUrl')}
                className="flex-1"
                data-testid="linkedin-url-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleExtractFromLinkedin();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleExtractFromLinkedin}
                disabled={isExtracting || !linkedinInput.trim()}
                className="shrink-0"
                aria-label={t('candidates.ai.extractFromLinkedin')}
                data-testid="linkedin-extract-button"
              >
                {isExtracting && extractionSource === 'linkedin' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>
            {isExtracting && extractionSource === 'linkedin' && (
              <div className="flex items-center gap-2 text-sm text-primary">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('candidates.ai.extracting')}
              </div>
            )}
          </div>
        </div>

        {/* Manual / Review Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">{t('candidates.fields.firstName')} *</Label>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: t('validation.nameRequired') }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="first_name"
                  className={cn(errors.first_name && 'border-destructive')}
                />
              )}
            />
            {errors.first_name && (
              <p className="text-sm text-destructive">{errors.first_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name">{t('candidates.fields.lastName')} *</Label>
            <Controller
              name="last_name"
              control={control}
              rules={{ required: t('validation.nameRequired') }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  className={cn(errors.last_name && 'border-destructive')}
                />
              )}
            />
            {errors.last_name && (
              <p className="text-sm text-destructive">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        <ValidatedEmailInput
          name="email"
          control={control}
          label={t('candidates.fields.email')}
        />

        {/* Job */}
        <Controller
          name="job_id"
          control={control}
          render={({ field }) => (
            <JobPicker
              value={field.value || ''}
              onChange={(id) => field.onChange(id || undefined)}
              label={t('candidates.fields.job')}
              allowClear
            />
          )}
        />

        {/* Source + conditional secondary picker on one row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('candidates.fields.source')}</Label>
            <Controller
              name="candidate_source"
              control={control}
              render={({ field }) => (
                <Select value={field.value || 'none'} onValueChange={(v) => field.onChange(v === 'none' ? undefined : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('common:app.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('common:app.none')}</SelectItem>
                    {CANDIDATE_SOURCES.map((source) => (
                      <SelectItem key={source} value={source}>
                        {t(`candidates.sources.${source}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Conditional: Employee Picker for referral/outbound */}
          {(watch('candidate_source') === 'employee_referral' || watch('candidate_source') === 'employee_outbound') && (
            <div className="space-y-2">
              <Label>{t('candidates.fields.referringEmployee')}</Label>
              <Controller
                name="referred_by_employee_id"
                control={control}
                render={({ field }) => (
                  <EmployeePicker
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          )}

          {/* Conditional: Organization Picker for agency/outsourcing */}
          {(watch('candidate_source') === 'agency' || watch('candidate_source') === 'outsourcing') && (
            <div className="space-y-2">
              <Label>{watch('candidate_source') === 'outsourcing' ? t('candidates.fields.outsourcingCompany') : t('candidates.fields.agencyCompany')}</Label>
              <Controller
                name="agency_organization_id"
                control={control}
                render={({ field }) => (
                  <OrganizationPicker
                    value={field.value || ''}
                    onChange={field.onChange}
                    organizationRelationshipFilter={watch('candidate_source') === 'outsourcing' ? 'outsourcing' : 'recruitment'}
                    placeholder={watch('candidate_source') === 'outsourcing' ? t('candidates.placeholders.outsourcingCompany') : t('candidates.placeholders.agencyCompany')}
                    addNewLabel={watch('candidate_source') === 'outsourcing' ? t('candidates.fields.addNewOutsourcing') : t('candidates.fields.addNewAgency')}
                    defaultRelationships={watch('candidate_source') === 'outsourcing' ? ['outsourcing'] : ['recruitment']}
                  />
                )}
              />
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {t('candidates.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
